import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';
import type ILicense from '@/components/interfaces/License';
import apiErrorMessage from '../../shared/apiErrors';

export type ProgramImageKind =
  | 'investigation_image' | 'isn_image' | 'it_image' | 'program_image'
  | 'reading_card_image' | 'se_image' | 'student_text_image';

export interface ProgramImage {
  file_name: string | null;
  kind: ProgramImageKind;
  thumb_url: string | null;
  url: string | null;
}

export interface EditableProgram {
  additional_copyrights: string[];
  archived: boolean;
  available_to_add: boolean;
  code: string;
  color: string | null;
  copyright: string | null;
  correlations_type: string | null;
  custom: boolean;
  depth_of_knowledge: boolean;
  description_content: string | null;
  edition_en: string | null;
  edition_es: string | null;
  font_size: string | null;
  full_title_with_edition: string;
  grade: number | null;
  has_narration: boolean;
  has_print_book: boolean;
  homeschool_email: string | null;
  id: number;
  images: ProgramImage[];
  integrated: boolean;
  licenses: ILicense[];
  materials_kit_order_url: string | null;
  notebook_border_color: string;
  order_url: string | null;
  refill_kit_order_url: string | null;
  retirement_year: number | null;
  science: boolean;
  show_complete_correlations: boolean;
  show_online_assessment: boolean;
  show_progressions: boolean;
  show_section_titles: boolean;
  show_source: boolean;
  subtitle: string | null;
  subtitle_es: string | null;
  tags: boolean;
  title: string;
  title_es: string | null;
}

// Each section sends only its own fields; the API permits exactly this set.
export type ProgramUpdatePayload = Partial<{
  additional_copyrights: string[];
  archived: boolean;
  available_to_add: boolean;
  code: string;
  color: string;
  copyright: string;
  correlations_type: string;
  depth_of_knowledge: boolean;
  description_content: string;
  edition_en: string;
  edition_es: string;
  font_size: string;
  grade: number | null;
  has_narration: boolean;
  has_print_book: boolean;
  homeschool_email: string;
  integrated: boolean;
  license_ids: number[];
  materials_kit_order_url: string;
  notebook_border_color: string;
  order_url: string;
  refill_kit_order_url: string;
  retirement_year: number | null;
  science: boolean;
  show_complete_correlations: boolean;
  show_online_assessment: boolean;
  show_progressions: boolean;
  show_section_titles: boolean;
  show_source: boolean;
  subtitle: string;
  subtitle_es: string;
  tags: boolean;
  title: string;
  title_es: string;
}>;

export const programQueryKey = (programId: number) => [QueryKeys.Programs, programId];

export const useProgram = (programId: number) => useQuery({
  queryFn: async () => {
    const { data } = await Axios.get<{ program: EditableProgram }>(
      Routes.plato_api_program_path(programId, { extra_attributes: ['licenses', 'images'] }),
    );
    return data.program;
  },
  queryKey: programQueryKey(programId),
});

export const useLicenses = () => useQuery({
  queryFn: async () => {
    const { data } = await Axios.get<{ data: ILicense[] }>(Routes.plato_api_licenses_path());
    return data.data;
  },
  queryKey: [QueryKeys.Licenses],
});

// The saved program comes back without its licenses, so the form refetches rather than patching
// the cache; the rail and programs table read titles and colors from ContentPrograms.
export const useUpdateProgram = (programId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (program: ProgramUpdatePayload) => Axios
      .put<{ data: EditableProgram }>(Routes.plato_api_program_path(programId), { program })
      .then(res => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: programQueryKey(programId) });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ContentPrograms] });
    },
  });
};

export type ProgramImageUploads = Partial<Record<'program_image' | 'reading_card_image', File>>;

// A create carries every section's fields at once, and its images when any were picked — as
// multipart then, since JSON can't carry the files. Arrays go as repeated program[key][] fields;
// nulls are left out so the column default applies.
const toFormData = (program: ProgramUpdatePayload, uploads: ProgramImageUploads) => {
  const form = new FormData();
  Object.entries(program).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (Array.isArray(value)) value.forEach(item => form.append(`program[${key}][]`, String(item)));
    else form.append(`program[${key}]`, String(value));
  });
  Object.entries(uploads).forEach(([kind, file]) => {
    if (file) form.append(`program[${kind}]`, file);
  });
  return form;
};

interface CreateProgramInput {
  program: ProgramUpdatePayload;
  uploads?: ProgramImageUploads;
}

export const useCreateProgram = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ program, uploads = {} }: CreateProgramInput) => {
      const body = Object.values(uploads).some(Boolean) ? toFormData(program, uploads) : { program };
      return Axios.post<{ data: EditableProgram }>(Routes.plato_api_programs_path(), body).then(res => res.data.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ContentPrograms] });
    },
  });
};

// Image uploads go as multipart form data, one field per picked file.
export const useUploadProgramImages = (programId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uploads: ProgramImageUploads) => {
      const form = new FormData();
      Object.entries(uploads).forEach(([kind, file]) => {
        if (file) form.append(`program[${kind}]`, file);
      });
      return Axios.put(Routes.plato_api_program_path(programId), form).then(res => res.data.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: programQueryKey(programId) });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ContentPrograms] });
    },
  });
};

export const updateErrorMessage = (error: unknown) => (
  apiErrorMessage(error, 'Couldn’t save the program. Try again.')
);
